import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ContextMenu,
  Table,
  TableColumnType,
  TabPanel,
  useDefaultTablePaging,
} from '@/components';
import { useUserContextSelector } from '@/redux';
import {
  AnnouncementLookupState,
  useSearchAnnouncementsQuery,
} from '@/redux/announcements';
import {
  AddAnnouncementButton,
  useDeleteAnnouncementAction,
  usePublishAnnouncementAction,
} from '@/views/Announcements.Details';
import { formatters } from '@/core';
import { SearchBar } from '@/components/SearchBar';
import { AnnouncementStatusBadge } from './AnnouncementStatusBadge';

function useColumns(
  openEdit: (id: string) => any,
  confirmDelete: (id: string) => any,
  publishAction: (id: string) => any,
) {
  const hasRole = useUserContextSelector((x) => x.hasRole);
  const canModify = hasRole('BFP_ADMIN');
  const { t } = useTranslation();

  return useMemo<TableColumnType<AnnouncementLookupState>[]>(() => {
    const columns: Array<TableColumnType<AnnouncementLookupState> | false> = [
      {
        title: t('articles.details.number'),
        key: 'number',
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.number.localeCompare(b.number),
        render: (_, item) => item.number,
      },
      {
        title: t('announcements.details.title'),
        key: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        render: (_, { id, title }) => (
          <NavLink to={`/admin/announcements/${id}`}>{title}</NavLink>
        ),
      },
      {
        title: t('announcements.details.startDate'),
        key: 'startDate',
        sorter: (a, b) =>
          (a.period.start ?? '').localeCompare(b.period.start ?? ''),
        render: (_, item) =>
          formatters.dateTime(item.period.start, 'date', {
            applyOffset: false,
          }),
      },
      {
        title: t('announcements.details.endDate'),
        key: 'endDate',
        sorter: (a, b) =>
          (a.period.end ?? '').localeCompare(b.period.end ?? ''),
        render: (_, item) =>
          item.period.end
            ? formatters.dateTime(item.period.end, 'date', {
                applyOffset: false,
              })
            : '-',
      },
      {
        title: t('announcements.details.status'),
        key: 'status',
        render: (_, item) => <AnnouncementStatusBadge value={item} />,
      },
      canModify && {
        title: t('announcements.details.actions'),
        key: 'actions',
        render: (_, { id, status }) => {
          const isPublished = status === 'Published';
          return (
            <ContextMenu
              items={
                [
                  !isPublished && {
                    onClick: () => publishAction(id),
                    label: t('announcements.action.publish'),
                  },
                  !isPublished && {
                    onClick: () => openEdit(id),
                    label: t('announcements.action.edit'),
                  },
                  !isPublished && {
                    onClick: () => confirmDelete(id),
                    label: t('announcements.action.delete'),
                  },
                ].filter(Boolean) as any
              }
            />
          );
        },
      },
    ];

    return columns.filter(
      (x) => !!x,
    ) as TableColumnType<AnnouncementLookupState>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, hasRole, openEdit, confirmDelete, publishAction]);
}

export function AnnouncementsListTabPanel() {
  const hasRole = useUserContextSelector((x) => x.hasRole);
  const canModify = hasRole('BFP_ADMIN');
  const { deleteAction } = useDeleteAnnouncementAction();
  const { publishAction } = usePublishAnnouncementAction();
  const push = useNavigate();
  const { setTotal, paging, params } = useDefaultTablePaging();
  const [searchString, setSearchString] = useState<string>();

  const { data: paged, isFetching } = useSearchAnnouncementsQuery({
    paging: params,
    searchString,
  });

  const items = useMemo(() => {
    if (paged) {
      setTotal(paged.total);
    }

    return paged?.items;
  }, [paged, setTotal]);

  const columns = useColumns(
    (id) => {
      push(`/admin/announcements/${id}/edit`);
    },
    deleteAction,
    publishAction,
  );

  const { t } = useTranslation();

  return (
    <TabPanel min="auto">
      <TabPanel.Title>{t('announcements.title')}</TabPanel.Title>
      <TabPanel.Actions>
        {canModify && <AddAnnouncementButton />}
      </TabPanel.Actions>
      <TabPanel.Body>
        <Table<AnnouncementLookupState>
          name="Announcements"
          loading={isFetching}
          columns={columns}
          dataSource={items}
          rowKey={(row) => row.id}
          pagination={paging}
          hideResetFiltersAction
          rightActions={
            <SearchBar
              onSearch={setSearchString}
              placeholder={t('announcements.search.placeholder')}
            />
          }
        />
      </TabPanel.Body>
    </TabPanel>
  );
}
